/**
 * Media swiper
 * - Click-to-play YouTube players are injected on demand (no iframe until asked).
 * - Inactive inline players are torn down so only one clip plays at a time.
 * - Fullscreen button opens a viewport-filling lightbox player.
 */
if (!customElements.get("media-swiper")) {
  customElements.define(
    "media-swiper",
    class MediaSwiper extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.lightbox = this.querySelector("[data-media-swiper-lightbox]");
        this._bodyOverflow = "";
        this._raf = 0;

        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onFullscreenChange = this._onFullscreenChange.bind(this);

        this.addEventListener("click", this._onClick);
        document.addEventListener("keydown", this._onKeydown);
        document.addEventListener("fullscreenchange", this._onFullscreenChange);
        document.addEventListener("webkitfullscreenchange", this._onFullscreenChange);

        this._observeSlides();
        this._bindCoverFallbacks();
      }

      disconnectedCallback() {
        this._initialized = false;
        this.removeEventListener("click", this._onClick);
        document.removeEventListener("keydown", this._onKeydown);
        document.removeEventListener("fullscreenchange", this._onFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", this._onFullscreenChange);

        this._slideObserver?.disconnect();
        this._slideObserver = null;
        cancelAnimationFrame(this._raf);

        this._clearInlineVideos();
        this._closeFullscreen(true);
      }

      /* ---------------- Slides ---------------- */

      _observeSlides() {
        const track = this.querySelector("[data-swiper-container]");
        if (!track || typeof MutationObserver === "undefined") return;

        this._slideObserver = new MutationObserver(() => {
          cancelAnimationFrame(this._raf);
          this._raf = requestAnimationFrame(() => {
            this._pauseInactiveInline();
            this._bindCoverFallbacks();
          });
        });

        // Swiper toggles state classes on slides; only watch class changes.
        this._slideObserver.observe(track, {
          subtree: true,
          attributes: true,
          attributeFilter: ["class"],
        });
      }

      _pauseInactiveInline() {
        this.querySelectorAll("[data-media-swiper-slide]").forEach((slide) => {
          if (slide.classList.contains("swiper-slide-active")) return;
          this._teardownFrame(slide);
        });
      }

      // maxresdefault.jpg does not exist for every video; degrade to hqdefault once.
      _bindCoverFallbacks() {
        this._boundCovers = this._boundCovers || new WeakSet();
        this._coverFallbackSrc = this._coverFallbackSrc || new WeakMap();

        this.querySelectorAll('[data-yt-cover="true"]').forEach((img) => {
          if (this._boundCovers.has(img)) return;
          this._boundCovers.add(img);

          img.addEventListener("error", () => {
            const slide = img.closest("[data-media-swiper-slide]");
            const id = slide?.getAttribute("data-video-id");
            if (!id) return;
            if (this._coverFallbackSrc.get(img) === id) return;
            this._coverFallbackSrc.set(img, id);
            img.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
          });
        });
      }

      /* ---------------- Playback ---------------- */

      _youtubeSrc(id) {
        const params = new URLSearchParams({
          autoplay: "1",
          playsinline: "1",
          rel: "0",
          modestbranding: "1",
          enablejsapi: "1",
          iv_load_policy: "3",
        });
        return `https://www.youtube.com/embed/${id}?${params.toString()}`;
      }

      _buildIframe(id, title) {
        const iframe = document.createElement("iframe");
        iframe.src = this._youtubeSrc(id);
        iframe.title = title || "YouTube video";
        iframe.setAttribute("frameborder", "0");
        iframe.setAttribute(
          "allow",
          "autoplay; encrypted-media; picture-in-picture; fullscreen",
        );
        iframe.setAttribute("allowfullscreen", "");
        return iframe;
      }

      _teardownFrame(slide) {
        const frame = slide?.querySelector("[data-media-swiper-frame]");
        if (!frame || frame.dataset.loaded !== "true") {
          slide?.classList.remove("is-playing");
          return;
        }
        frame.innerHTML = "";
        frame.dataset.loaded = "false";
        slide.classList.remove("is-playing");
      }

      _clearInlineVideos(except) {
        this.querySelectorAll('[data-media-swiper-frame][data-loaded="true"]').forEach(
          (frame) => {
            if (except && frame === except) return;
            this._teardownFrame(frame.closest("[data-media-swiper-slide]"));
          },
        );
      }

      _playInline(slide) {
        if (!slide) return;
        const id = slide.getAttribute("data-video-id");
        const frame = slide.querySelector("[data-media-swiper-frame]");
        if (!id || !frame) return;

        // Only one inline clip at a time.
        this._clearInlineVideos(frame);
        if (frame.dataset.loaded === "true") return;

        const title = slide.querySelector(".media-swiper__caption-title");
        frame.appendChild(this._buildIframe(id, title?.textContent?.trim()));
        frame.dataset.loaded = "true";
        slide.classList.add("is-playing");
      }

      /* ---------------- Fullscreen ---------------- */

      _openFullscreen(slide) {
        if (!this.lightbox || !slide) return;
        const id = slide.getAttribute("data-video-id");
        if (!id) return;

        const frame = this.lightbox.querySelector(
          "[data-media-swiper-lightbox-frame]",
        );
        if (!frame) return;

        frame.innerHTML = "";
        const title = slide.querySelector(".media-swiper__caption-title");
        frame.appendChild(this._buildIframe(id, title?.textContent?.trim()));

        this.lightbox.hidden = false;
        this._bodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        this.lightbox.querySelector(".media-swiper__lightbox-inner")?.focus();

        // Prefer the real browser fullscreen; the fixed overlay already fills
        // the viewport when the API is unavailable (iOS Safari, sandboxed editors).
        this._requestNativeFullscreen();
      }

      _closeFullscreen(force) {
        if (!this.lightbox) return;
        if (!force && this.lightbox.hidden) return;

        // Hide first so the resulting fullscreenchange event is a no-op.
        this.lightbox.hidden = true;
        this.lightbox.classList.remove("is-fallback");
        const frame = this.lightbox.querySelector(
          "[data-media-swiper-lightbox-frame]",
        );
        if (frame) frame.innerHTML = "";

        document.body.style.overflow = this._bodyOverflow || "";
        this._bodyOverflow = "";

        this._exitNativeFullscreen();
      }

      _requestNativeFullscreen() {
        const el = this.lightbox;
        if (!el) return;

        // Touch devices have no ESC key, and Android Chrome's back gesture does
        // not always leave element fullscreen - so always expose the close button.
        const needsCloseButton =
          typeof window.matchMedia === "function" &&
          window.matchMedia("(hover: none) and (pointer: coarse)").matches;

        if (needsCloseButton) el.classList.add("is-fallback");

        const request = el.requestFullscreen || el.webkitRequestFullscreen;

        // iOS Safari has no Element.requestFullscreen: the fixed overlay is all
        // we get, so fall back to showing the close button.
        if (typeof request !== "function") {
          el.classList.add("is-fallback");
          return;
        }

        try {
          const result = request.call(el);
          if (result && typeof result.then === "function") {
            result
              .then(() => {
                if (!needsCloseButton) el.classList.remove("is-fallback");
              })
              .catch(() => el.classList.add("is-fallback"));
          }
          // Some engines resolve without a promise; verify after the fact.
          window.setTimeout(() => {
            if (
              !el.hidden &&
              !needsCloseButton &&
              !(document.fullscreenElement || document.webkitFullscreenElement)
            ) {
              el.classList.add("is-fallback");
            }
          }, 300);
        } catch (_) {
          el.classList.add("is-fallback");
        }
      }

      _exitNativeFullscreen() {
        const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
        if (!fsEl) return;
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (typeof exit !== "function") return;
        try {
          const result = exit.call(document);
          if (result && typeof result.catch === "function") result.catch(() => {});
        } catch (_) {
          /* ignore */
        }
      }

      _onFullscreenChange() {
        const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
        if (!fsEl && this.lightbox && !this.lightbox.hidden) {
          this._closeFullscreen();
        }
      }

      /* ---------------- Events ---------------- */

      _onClick(event) {
        const play = event.target.closest("[data-media-swiper-play]");
        if (play) {
          event.preventDefault();
          this._playInline(play.closest("[data-media-swiper-slide]"));
          return;
        }

        const fullscreen = event.target.closest("[data-media-swiper-fullscreen]");
        if (fullscreen) {
          event.preventDefault();
          this._openFullscreen(fullscreen.closest("[data-media-swiper-slide]"));
          return;
        }

        const close = event.target.closest("[data-media-swiper-close]");
        if (close) {
          event.preventDefault();
          this._closeFullscreen();
          return;
        }

        if (this.lightbox && event.target === this.lightbox) {
          this._closeFullscreen();
        }
      }

      _onKeydown(event) {
        if (event.key !== "Escape") return;
        if (this.lightbox && !this.lightbox.hidden) {
          event.preventDefault();
          this._closeFullscreen();
        }
      }
    },
  );
}
